import React, { useState } from 'react';
import VerificationPhoneInput from './VerificationPhoneInput';
import VerificationNumberInput from './VerificationNumberInput';
import IntlMessages from 'Util/IntlMessages';
import { useMutationQuery } from 'Api/hooks/useMutationQuery';
import { useSelector } from 'react-redux';
import { isBlank } from 'Util/DataUtils';
import { joinError, WRONG_FORMAT_PHONE } from 'Api/Errors';
import { useIntl } from 'react-intl';
import { usePopup } from 'Util/usePopup';
import FormatNumberInput from '../FormatNumberInput';
import { BUSINESS_TYPE, VERIFY_TYPE } from 'Routes/sign-in/components/LeftSide/constants';

const VerificationForm = ({ type = VERIFY_TYPE.SIGN_UP, businessType = BUSINESS_TYPE.INDIVIDUAL, goToNext }) => {
  const { formatMessage } = useIntl();
  const { country } = useSelector(state => state.settings);
  const [businessNumber, setBusinessNumber] = useState('');
  const [phone, setPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [hasSendPhone, setHasSendPhone] = useState(false);
  const [error, setError] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const { showOneButtonSuccessDialog } = usePopup();
  const phoneCountry = country === 'vi' ? '84' : '82';

  const { mutate: checkDuplicatePhoneMutate } = useMutationQuery({
    method: 'post',
    url: '/auth/checkDuplicatePhone',
    options: {
      onSuccess: ({ data }) => {
        if (data.result) {
          sendPhoneMutate({ type, phoneCountry, phone, ...(type === VERIFY_TYPE.FIND_PASSWORD && { businessNumber: businessType === BUSINESS_TYPE.FREELANCER ? phone : businessNumber }) });
        } else {
          setError(formatMessage({ id: 'error.PHONE_NUMBER_EXISTS' }));
        }
      },
      onError: error => {
        const message = error?.response?.data?.message;
        setError(message);
      },
    },
  });

  const { mutate: sendPhoneMutate } = useMutationQuery({
    method: 'post',
    url: '/auth/phoneVerification/send',
    options: {
      onSuccess: ({ data }) => {
        setExpiresAt(data.expiresAt);
        setOtpCode('');
        setHasSendPhone(true);
        showOneButtonSuccessDialog({ title: hasSendPhone ? 'signIn.join.phone.verify.resend.message' : 'signIn.join.phone.verify.send.message' });
      },
      onError: error => {
        const message = error?.response?.data?.message;
        setError(message);
      },
    },
  });
  const { mutate: verifyPhoneMutate } = useMutationQuery({
    method: 'post',
    url: '/auth/phoneVerification/verify',
    options: {
      onSuccess: () => {
        showOneButtonSuccessDialog({
          title:
            type === VERIFY_TYPE.FIND_PASSWORD
              ? businessType === BUSINESS_TYPE.FREELANCER
                ? 'signIn.join.phone.verify.complete.message.findPassword.freelancer'
                : 'signIn.join.phone.verify.complete.message.findPassword'
              : 'signIn.join.phone.verify.complete.message',
          onClose: () => goToNext({ phone, otpCode, ...(type === VERIFY_TYPE.FIND_PASSWORD && { businessNumber: businessType === BUSINESS_TYPE.FREELANCER ? phone : businessNumber }) }),
        });
      },
      onError: error => {
        const message = error?.response?.data?.message;
        setError(message);
      },
    },
  });

  const handleSendPhone = () => {
    setHasSendPhone(false);
    setError(null);
    if (isBlank(phone) || phone.length !== 11) {
      setError(
        formatMessage({ id: type === VERIFY_TYPE.FIND_PASSWORD && businessType === BUSINESS_TYPE.INDIVIDUAL ? 'error.signIn.join.findPassword.businessNumber' : joinError[WRONG_FORMAT_PHONE] })
      );
      return;
    }
    if (type === VERIFY_TYPE.FIND_PASSWORD && businessType === BUSINESS_TYPE.INDIVIDUAL && isBlank(businessNumber)) {
      setError(formatMessage({ id: 'error.signIn.join.findPassword.businessNumber' }));
      return;
    }

    if (type !== VERIFY_TYPE.FIND_PASSWORD && businessType === BUSINESS_TYPE.FREELANCER) {
      checkDuplicatePhoneMutate({ phoneCountry, businessNumber: phone });
      return;
    }
    sendPhoneMutate({ type, phoneCountry, phone, ...(type === VERIFY_TYPE.FIND_PASSWORD && { businessNumber: businessType === BUSINESS_TYPE.FREELANCER ? phone : businessNumber }) });
  };

  const handleVerifyPhone = () => {
    verifyPhoneMutate({ type, phoneCountry, phone, otpCode });
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
      }}
    >
      {type === VERIFY_TYPE.FIND_PASSWORD && businessType === BUSINESS_TYPE.INDIVIDUAL && (
        <FormatNumberInput
          isError={!hasSendPhone && !!error}
          format="###-##-#####"
          label="signIn.join.findPassword.businessNumber"
          placeholder="signIn.join.findPassword.businessNumber"
          value={businessNumber}
          onValueChange={({ value }) => {
            setError(null);
            setHasSendPhone(false);
            setBusinessNumber(value);
          }}
          autoFocus
        />
      )}
      <VerificationPhoneInput
        isError={!hasSendPhone && !!error}
        errorText={error}
        value={phone}
        onValueChange={({ value }) => {
          setError(null);
          setHasSendPhone(false);
          setPhone(value);
        }}
        hasSendPhone={hasSendPhone}
        handleSendPhone={handleSendPhone}
        autoFocus={type !== VERIFY_TYPE.FIND_PASSWORD || businessType === BUSINESS_TYPE.FREELANCER}
      />
      {hasSendPhone && (
        <>
          <VerificationNumberInput
            isError={!!error}
            errorText={error}
            expiresAt={expiresAt}
            value={otpCode}
            onValueChange={({ value }) => {
              setError(null);
              setOtpCode(value);
            }}
            handleVerifyPhone={otpCode.length !== 6 ? () => {} : handleVerifyPhone}
          />
          <button
            className={`${otpCode.length === 6 ? 'cursor-pointer no-drag' : ''}`}
            style={{
              height: '50px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: otpCode.length === 6 ? '#0567f9' : '#B2B2B2',
              color: '#fff',
              borderRadius: '10px',
              fontSize: '15px',
              fontWeight: 700,
              lineHeight: 'normal',
              border: 'none',
              marginTop: '30px',
            }}
            disabled={otpCode.length !== 6}
            onClick={handleVerifyPhone}
          >
            <IntlMessages id="consult.button.next" />
          </button>
        </>
      )}
    </div>
  );
};

export default VerificationForm;
